import { useEffect, useState, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { ROUTES } from "../../constants";
import { hydrateUserFromStorage } from "../../Redux/Slices/authSlice";
import { fetchMe } from "../../Redux/Thunks/AuthThunks";
import { saveAuthReturnUrl } from "../../utils/authReturnUrl";

const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const authToken = localStorage.getItem("authToken");
  const user = useSelector((state) => state.auth?.user);
  const me = useSelector((state) => state.auth?.me);
  const fetchMeLoading = useSelector((state) => state.auth?.loading?.fetchMe);
  const [hasAttemptedHydrate, setHasAttemptedHydrate] = useState(false);
  const fetchMeAttemptedRef = useRef(false);

  // If token exists but user is not in Redux, hydrate from localStorage (no GET /auth/profile)
  useEffect(() => {
    if (authToken && !user && !hasAttemptedHydrate) {
      try {
        const tokenData = JSON.parse(authToken);
        if (tokenData?.accessToken || tokenData?.access_token) {
          setHasAttemptedHydrate(true);
          dispatch(hydrateUserFromStorage());
        } else {
          setHasAttemptedHydrate(true);
        }
      } catch (error) {
        console.error("Error parsing auth token:", error);
        setHasAttemptedHydrate(true);
      }
    }
  }, [authToken, user, hasAttemptedHydrate, dispatch]);

  // Token + user in Redux (e.g. after refresh) but profile not loaded — GET /users/me once
  useEffect(() => {
    if (!authToken || !user) return;
    if (me != null) return;
    if (fetchMeLoading) return;
    if (fetchMeAttemptedRef.current) return;
    try {
      const tokenPayload = JSON.parse(authToken);
      if (tokenPayload?.requirePasswordChange === true) {
        fetchMeAttemptedRef.current = true;
        return;
      }
    } catch {
      /* continue */
    }
    fetchMeAttemptedRef.current = true;
    dispatch(fetchMe());
  }, [authToken, user, me, fetchMeLoading, dispatch]);

  // If no token, redirect to login and restore this URL after sign-in
  if (!authToken) {
    saveAuthReturnUrl(location);
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  // Must change password before using the app — avoid GET /users/me and keep user off guarded routes
  try {
    const tokenPayload = JSON.parse(authToken);
    if (tokenPayload?.requirePasswordChange === true) {
      return <Navigate to={ROUTES.FORCE_PASSWORD} replace />;
    }
  } catch {
    /* invalid JSON — continue with normal auth flow */
  }

  // Brief loading while we hydrate user from localStorage on refresh
  if (authToken && !user && !hasAttemptedHydrate) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block h-8 w-8 border-4 border-theme-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  // If token exists, allow access (user will be loaded asynchronously)
  // This fixes the refresh issue - we trust the token for authentication
  return children;
};

export default ProtectedRoute;
